// Client side search over the manual. Everything the index needs already arrives
// in Redux from PAGES_LISTENER, so there is no backend, no new collection and no
// search dependency. buildSearchIndex walks pages.pages once per corpus change;
// searchIndex does the term-wise match. The query lives in the URL, not in the
// store, so results survive a refresh and stay shareable.

import {topLevelPage} from './topLevelPage';

export const SEARCH_RESULT_LIMIT = 50;

const SNIPPET_RADIUS = 80;

export const queryParam = (search, key) => {
  if (!search) return '';
  const params = new URLSearchParams(search);
  const value = params.get(key);
  return value === null ? '' : value;
}

// block has to survive a bad link: a typo falls back to null and the caller
// lands on the top of the page rather than throwing or picking nothing.
export const blockParam = search => {
  const raw = queryParam(search, 'block');
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
};

// A detached textarea decodes character references as part of its own content
// model and does not parse nested markup, so this stays a text transform and
// never evaluates anything in the string.
export const decodeEntities = text => {
  if (!text) return '';
  if (typeof document === 'undefined') return text;
  const area = document.createElement('textarea');
  area.innerHTML = text;
  return area.value;
};

export const stripHtml = html => {
  if (!html) return '';
  const withoutTags = String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|blockquote|pre|ul|ol)>/gi, ' ')
    .replace(/<[^>]*>/g, '');
  return decodeEntities(withoutTags).replace(/\s+/g, ' ').trim();
};

const countOccurrences = (haystack, needle, cap = 50) => {
  if (!haystack || !needle) return 0;
  let count = 0;
  let at = haystack.indexOf(needle);
  while (at !== -1 && count < cap) {
    count += 1;
    at = haystack.indexOf(needle, at + needle.length);
  }
  return count;
};

// blockIndex is the position of the block in ContentPane's sorted array, not the
// key of contentBlocks. AdminPageEdit deletes middle keys, so the two differ as
// soon as a block has been removed, and ContentPane derives the DOM id from the
// position (content-block-${index}). A key here would deep link to the wrong block.
export const buildSearchIndex = allPages => {
  const records = [];
  if (!allPages) return records;

  Object.keys(allPages).forEach(id => {
    const page = allPages[id];
    if (!page || !page.slug) return;

    const title = page.title || '';
    const titleLower = title.toLowerCase();
    const top = topLevelPage(page, allPages);
    const sectionTitle = top && top.title ? top.title : '';

    records.push({
      id,
      slug: page.slug,
      title,
      sectionTitle,
      blockIndex: null,
      header: '',
      titleLower,
      headerLower: '',
      bodyLower: '',
      contentHTML: null,
    });

    const blocks = page.contentBlocks;
    if (!blocks) return;

    const ordered = Object.keys(blocks)
      .map(key => ({key, index: Number(key)}))
      .filter(entry => Number.isSafeInteger(entry.index))
      .sort((a, b) => a.index - b.index);

    ordered.forEach((entry, blockIndex) => {
      const block = blocks[entry.key];
      if (!block) return;
      const header = block.header || '';
      const contentHTML = block.contentHTML || null;

      records.push({
        id,
        slug: page.slug,
        title,
        sectionTitle,
        blockIndex,
        header,
        titleLower,
        headerLower: header.toLowerCase(),
        bodyLower: contentHTML ? contentHTML.toLowerCase() : '',
        contentHTML,
      });
    });
  });

  return records;
};

const scoreRecord = (record, normalized, terms) => {
  const title = record.titleLower || '';
  const header = record.headerLower || '';
  const body = record.bodyLower || '';

  let tier;
  if (title === normalized) tier = 0;
  else if (title.startsWith(normalized)) tier = 1;
  else if (terms.every(term => title.includes(term))) tier = 2;
  else if (header && header === normalized) tier = 3;
  else if (header && terms.every(term => header.includes(term))) tier = 4;
  else tier = 5;

  const phrase =
    title.includes(normalized) || (header && header.includes(normalized)) || body.includes(normalized)
      ? 1
      : 0;

  const bodyHits =
    tier === 5 ? terms.reduce((total, term) => total + countOccurrences(body, term), 0) : 0;

  return {tier, phrase, bodyHits};
};

const blockKey = record => (record.blockIndex === null ? -1 : record.blockIndex);

// Matching runs against the raw lowercased contentHTML rather than a stripped
// copy, so there is no strip pass at index time. Quill splits at word boundaries,
// so a single term is never straddled across a tag and the raw string is enough.
// A page-level match is shared by every record on that page, because they all
// carry the same title. Without this, a title-only query lists the page once per
// block, each row pointing at a block whose text never contained the term and
// each snippet falling back to an unmarked opening. Keep the page-level record
// and only the blocks that match on their own header or body.
const matchesOwnText = (record, terms) => {
  const headerHit = record.headerLower && terms.every(term => record.headerLower.includes(term));
  const bodyHit = record.bodyLower && terms.every(term => record.bodyLower.includes(term));
  return Boolean(headerHit || bodyHit);
};

export const searchIndex = (records, query) => {
  const normalized = (query || '').trim().toLowerCase();
  if (!normalized || !records || !records.length) return [];

  const terms = normalized.split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  const scored = records.reduce((acc, record) => {
    const haystack = record.titleLower + ' ' + record.headerLower + ' ' + record.bodyLower;
    if (!terms.every(term => haystack.includes(term))) return acc;
    acc.push({record, score: scoreRecord(record, normalized, terms)});
    return acc;
  }, []);

  const pagesMatched = new Set(
    scored.filter(entry => entry.record.blockIndex === null).map(entry => entry.record.slug)
  );

  const kept = scored.filter(entry => {
    const record = entry.record;
    if (record.blockIndex === null) return true;
    if (!pagesMatched.has(record.slug)) return true;
    return matchesOwnText(record, terms);
  });

  kept.sort((a, b) => {
    if (a.score.tier !== b.score.tier) return a.score.tier - b.score.tier;
    if (a.score.phrase !== b.score.phrase) return b.score.phrase - a.score.phrase;
    if (a.score.bodyHits !== b.score.bodyHits) return b.score.bodyHits - a.score.bodyHits;
    const byTitle = a.record.title.localeCompare(b.record.title, undefined, {sensitivity: 'base'});
    if (byTitle) return byTitle;
    return blockKey(a.record) - blockKey(b.record);
  });

  return kept.map(entry => entry.record);
};

// Split rather than sliced, so the count line stays truthful when a short term
// matches more than the page will render. Callers cap what they display.
export const snippetAround = (text, terms, normalized, radius = SNIPPET_RADIUS) => {
  if (!text) return {before: '', match: '', after: ''};

  const haystack = text.toLowerCase();
  let index = normalized ? haystack.indexOf(normalized) : -1;
  let length = index === -1 ? 0 : normalized.length;

  if (index === -1) {
    terms.forEach(term => {
      const at = haystack.indexOf(term);
      if (at !== -1 && (index === -1 || at < index)) {
        index = at;
        length = term.length;
      }
    });
  }

  if (index === -1) {
    return {before: '', match: '', after: text.slice(0, radius * 2)};
  }

  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + length + radius);

  return {
    before: (start > 0 ? '…' : '') + text.slice(start, index),
    match: text.slice(index, index + length),
    after: text.slice(index + length, end) + (end < text.length ? '…' : ''),
  };
};

export default searchIndex;
